import dayjs from 'dayjs';
import { CallQualityV1 } from '@frontend/api-call-quality';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Button, Heading, SkeletonLoader, Text } from '@frontend/design-system';
import { CallDetailsContext, RecentCallRecord } from '../types';
import {
  TimelineSection,
  IssuesDetectedSection,
  MetricSection,
  RecommendationsSection,
  CallQualityFeedback,
} from './call-quality-components';
import { createMetricData } from './call-quality-utils';
import { PanelHeader } from './common';
import { SCREENS } from './const';
import { MetricData, ScreenBase } from './types';

const CallLabel = ({ record }: { record: RecentCallRecord }) => {
  const { t } = useTranslation('calls');

  const contact = record.callerName || record.callerNumber;
  const icon =
    record.direction === 'outbound' ? (
      <Icon color='light' name='phone-outgoing-small' />
    ) : (
      <Icon color='light' name='phone-incoming-small' />
    );
  const label =
    record.direction === 'outbound'
      ? t('Call to {{name}}', { name: contact })
      : t('Call from {{name}}', { name: contact });

  return (
    <div
      style={{
        gridArea: 'label',
        display: 'grid',
        alignItems: 'center',
        gap: theme.spacing(1),
        gridTemplateColumns: 'repeat(4, minmax(0px,max-content))',
      }}
    >
      {icon}
      <Text as='span' size='medium'>
        {label}
      </Text>
      <Text as='span' size='medium'>
        •
      </Text>
      <Text as='span' size='medium'>
        {dayjs(record.answeredAt).format('ddd MMM D, YYYY, h:mm A')}
      </Text>
    </div>
  );
};

/**
 * @deprecated
 * This component is deprecated and should not be used in new code.
 * Please use the new CallQualityScreen implementation.
 */

export const CallQualityScreenOld = ({ setScreen }: ScreenBase) => {
  const { t } = useTranslation('calls');
  const { context } = useSlidePanelShallowStore<CallDetailsContext>('setShow', 'context', 'show');
  const row = context?.rows.find((row) => row.id === context.currentRowId);

  const { data, isLoading } = CallQualityV1.Queries.useGetCallQualityMetricsQuery({
    request: {
      callStartTime: row?.answeredAt || '',
      channelId: row?.channelId || '',
    },
    options: {
      enabled: !!(row?.answeredAt && row.channelId),
    },
  });

  const metricData: MetricData[] = [
    {
      type: 'mosMin',
      values: [
        createMetricData({
          type: 'mos',
          value: data?.minMosScore || 0,
        }),
      ],
    },
    {
      type: 'packetLossMax',
      values: [
        createMetricData({
          type: 'packetLoss',
          value: data?.maxPacketLossPercentage.inbound || 0,
          direction: 'inbound',
        }),
        createMetricData({
          type: 'packetLoss',
          value: data?.maxPacketLossPercentage.outbound || 0,
          direction: 'outbound',
        }),
      ],
    },
    {
      type: 'jitterMax',
      values: [
        createMetricData({
          type: 'jitter',
          value: data?.maxJitterInMillisecond.inbound || 0,
          direction: 'inbound',
        }),
        createMetricData({
          type: 'jitter',
          value: data?.maxJitterInMillisecond.outbound || 0,
          direction: 'outbound',
        }),
      ],
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <PanelHeader setScreen={setScreen}>
        <div
          style={{
            display: 'grid',
            alignItems: 'center',
            columnGap: theme.spacing(1),
            gridTemplateColumns: 'auto 1fr',
            gridTemplateAreas: '"back title" ". label"',
          }}
        >
          <Button
            iconName='caret-left'
            variant='secondary'
            css={{ gridArea: 'back' }}
            onClick={() => {
              setScreen({ screen: SCREENS.CALL_DETAILS });
            }}
          />

          <Heading level={2} css={{ margin: 0, gridArea: 'title' }}>
            {t('Network Performance')}
          </Heading>
          {row && <CallLabel record={row} />}
        </div>
      </PanelHeader>
      {data && !isLoading && (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: 'repeat(auto-fit, auto)',
            padding: theme.spacing(3),
            gap: theme.spacing(2),
          }}
        >
          <IssuesDetectedSection variant='screen' />
          <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
          <RecommendationsSection />
          <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
          <MetricSection data={metricData} subtitle={t('These values highlight the worst call quality detected.')} />
          <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
          <TimelineSection
            blips={data.blips ?? []}
            startTime={data?.callAnsweredTime}
            endTime={data?.callEndTime}
            onClick={(_data) => {
              setScreen({
                screen: SCREENS.CALL_QUALITY_DRILLDOWN,
                data: {
                  ..._data,
                  startTime: data?.callAnsweredTime,
                  channelId: row?.channelId ?? '',
                  locationId: row?.locationId ?? '',
                },
              });
            }}
          />
          <CallQualityFeedback
            startTime={data?.callAnsweredTime}
            channelId={row?.channelId ?? ''}
            locationId={row?.locationId ?? ''}
          />
        </div>
      )}
      {isLoading && (
        <div
          style={{
            height: '100%',
            padding: theme.spacing(3),
            display: 'grid',
            gridTemplateRows: 'repeat(3, max-content)',
            gap: theme.spacing(2),
          }}
        >
          <SkeletonLoader height={150} width={450} />
          <SkeletonLoader height={300} width={450} />
          <SkeletonLoader height={150} width={450} />
        </div>
      )}
      {!data && !isLoading && (
        <Text size='medium' css={{ textAlign: 'center', marginTop: theme.spacing(4) }}>
          {t('No call quality data available for this call.')}
        </Text>
      )}
    </div>
  );
};
